import type {
  InviteLink,
  WorkspaceInviteLinkExpireTime,
} from '@affine/graphql';
import { Permission } from '@affine/graphql';
import { useI18n } from '@affine/i18n';
import { EmailIcon, LinkIcon } from '@blocksuite/icons/rc';

import { Menu, MenuItem, MenuTrigger } from '../../../ui/menu';
import { RadioGroup } from '../../../ui/radio';
import { EmailInvite } from './email-invite';
import { LinkInvite } from './link-invite';
import * as styles from './styles.css';

export type InviteMethodType = 'email' | 'link';
export const ModalContent = ({
  inviteEmail,
  setInviteEmail,
  inviteMethod,
  permission,
  onInviteMethodChange,
  onPermissionChange,
  handleConfirm,
  isMutating,
  isValidEmail,
  copyTextToClipboard,
  onGenerateInviteLink,
  onRevokeInviteLink,
  importCSV,
  invitationLink,
}: {
  inviteEmail: string;
  importCSV: React.ReactNode;
  invitationLink: InviteLink | null;
  setInviteEmail: (value: string) => void;
  inviteMethod: InviteMethodType;
  permission: Permission;
  onInviteMethodChange: (value: InviteMethodType) => void;
  onPermissionChange: (value: Permission) => void;
  handleConfirm: () => void;
  isMutating: boolean;
  isValidEmail: boolean;
  copyTextToClipboard: (text: string) => Promise<boolean>;
  onGenerateInviteLink: (
    expireTime: WorkspaceInviteLinkExpireTime
  ) => Promise<string>;
  onRevokeInviteLink: () => Promise<boolean>;
}) => {
  const t = useI18n();

  return (
    <div className={styles.modalContent}>
      <div>{t['com.streamlain.payment.member.team.invite.description']()}</div>
      <RadioGroup
        width={'100%'}
        value={inviteMethod}
        onChange={onInviteMethodChange}
        items={[
          {
            label: (
              <RadioItem
                icon={<EmailIcon className={styles.iconStyle} />}
                label={t[
                  'com.streamlain.payment.member.team.invite.email-invite'
                ]()}
              />
            ),
            value: 'email',
          },
          {
            label: (
              <RadioItem
                icon={<LinkIcon className={styles.iconStyle} />}
                label={t[
                  'com.streamlain.payment.member.team.invite.invite-link'
                ]()}
              />
            ),
            value: 'link',
          },
        ]}
      />
      {inviteMethod === 'email' ? (
        <>
          <div className={styles.inviteRoleField}>
            <div className={styles.modalSubTitle}>
              {t['com.streamlain.settings.workspace.mcp-access.row.role']()}
            </div>
            <Menu
              contentOptions={{ align: 'end' }}
              items={[
                Permission.Admin,
                Permission.Editor,
                Permission.Reader,
              ].map(role => (
                <MenuItem
                  key={role}
                  data-testid={`invite-member-role-option-${role}`}
                  onSelect={() => onPermissionChange(role)}
                >
                  {roleLabel(t, role)}
                </MenuItem>
              ))}
            >
              <MenuTrigger
                block
                disabled={isMutating}
                data-testid="invite-member-role-trigger"
              >
                {roleLabel(t, permission)}
              </MenuTrigger>
            </Menu>
          </div>
          <EmailInvite
            inviteEmail={inviteEmail}
            setInviteEmail={setInviteEmail}
            handleConfirm={handleConfirm}
            isMutating={isMutating}
            isValidEmail={isValidEmail}
            importCSV={importCSV}
          />
        </>
      ) : (
        <LinkInvite
          invitationLink={invitationLink}
          copyTextToClipboard={copyTextToClipboard}
          generateInvitationLink={onGenerateInviteLink}
          revokeInvitationLink={onRevokeInviteLink}
        />
      )}
    </div>
  );
};

const roleLabel = (t: ReturnType<typeof useI18n>, role: Permission) => {
  switch (role) {
    case Permission.Admin:
      return t['com.streamlain.settings.workspace.permissions.role.admin']();
    case Permission.Editor:
      return t['com.streamlain.settings.workspace.permissions.role.editor']();
    case Permission.Reader:
      return t['com.streamlain.settings.workspace.permissions.role.reader']();
    default:
      return role;
  }
};

const RadioItem = ({
  icon,
  label,
}: {
  icon: React.ReactNode;
  label: string;
}) => {
  return (
    <div className={styles.radioItem}>
      {icon}
      <div>{label}</div>
    </div>
  );
};
